import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import VerifyLicenseForHPEPage from "@/pages/VerifyLicenseForHPEPage";
import AdminHPEVerificationsPage from "@/pages/admin/AdminHPEVerificationsPage";
import { CheckoutGate } from "@/components/auth/CheckoutGate";
import { GlobalDisclaimer } from "@/components/layout/GlobalDisclaimer";
import AskPage from "./pages/AskPage"; // eager: LCP route — consultation is the landing experience

const HomePage = lazy(() => import("./pages/HomePage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const TransformationsPage = lazy(() => import("./pages/TransformationsPage"));
const LeaderboardsPage = lazy(() => import("./pages/LeaderboardsPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const AnswerPage = lazy(() => import("./pages/AnswerPage"));
const SavedPage = lazy(() => import("./pages/SavedPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const GuidePage = lazy(() => import("./pages/GuidePage"));
import { guides } from "@/lib/guides";
const ProDecisionMode = lazy(() => import("./pages/ProDecisionMode"));
const BROCheck = lazy(() => import("./pages/BROCheck"));
const HUGFinish = lazy(() => import("./pages/HUGFinish"));
const KeepLifting = lazy(() => import("./pages/KeepLifting"));
const FinishDecision = lazy(() => import("./pages/FinishDecision"));
const ICEFinish = lazy(() => import("./pages/ICEFinish"));
const AssistantPage = lazy(() => import("./pages/AssistantPage"));
const HairRescuePage = lazy(() => import("./pages/HairRescuePage"));
const ClientRecordsPage = lazy(() => import("./pages/ClientRecordsPage"));
const SalonOwnerPortal = lazy(() => import("./pages/SalonOwnerPortal"));
const SalonOwnerLicensePage = lazy(() => import("./pages/SalonOwnerLicensePage"));
const SalonCheckoutPage = lazy(() => import("./pages/SalonCheckoutPage"));
const CheckoutSalonStarter = lazy(() => import("./pages/checkout/CheckoutSalonStarter"));
const CheckoutSalonProfessional = lazy(() => import("./pages/checkout/CheckoutSalonProfessional"));
const CheckoutSalonElite = lazy(() => import("./pages/checkout/CheckoutSalonElite"));
const SalonPortalPage = lazy(() => import("./pages/salon/SalonPortalPage"));
const LicenseActivatedPage = lazy(() => import("./pages/LicenseActivatedPage"));
const VerifyProfessionalLicense = lazy(() => import("./pages/VerifyProfessionalLicense"));
const MyStylistsPage = lazy(() => import("./pages/salon/MyStylistsPage"));
const SalonTrainingPage = lazy(() => import("./pages/salon/SalonTrainingPage"));
const SalonBrandingPage = lazy(() => import("./pages/salon/SalonBrandingPage"));
const WhenTrainingIsntTrainingPage = lazy(() => import("./pages/salon/WhenTrainingIsntTrainingPage"));
const CompetitionsPage = lazy(() => import("./pages/salon/CompetitionsPage"));
const HiringBoardPage = lazy(() => import("./pages/salon/HiringBoardPage"));
const FixItChallengePage = lazy(() => import("./pages/FixItChallengePage"));
const FixItArenaPage = lazy(() => import("./pages/FixItArenaPage"));
const FixItBattlePage = lazy(() => import("./pages/FixItBattlePage"));
const AcademyLeaderboardPage = lazy(() => import("./pages/AcademyLeaderboardPage"));
const AcademyProfilePage = lazy(() => import("./pages/AcademyProfilePage"));
const StylistSkillProfilePage = lazy(() => import("./pages/StylistSkillProfilePage"));
const VerificationDirectoryPage = lazy(() => import("./pages/VerificationDirectoryPage"));
const ModelReleaseFormPage = lazy(() => import("./pages/ModelReleaseFormPage"));
const CompetitionEntriesPage = lazy(() => import("./pages/CompetitionEntriesPage"));
const StarlieLandingPage = lazy(() => import("./pages/StarlieLandingPage"));
const MyPathPage = lazy(() => import("./pages/MyPathPage"));
const VotingPage = lazy(() => import("./pages/VotingPage"));
const PermMethodPage = lazy(() => import("./pages/PermMethodPage"));
const BackOfficePage = lazy(() => import("./pages/BackOfficePage"));
const AdminBackOfficePage = lazy(() => import("./pages/AdminBackOfficePage"));
const ChallengeHPEPage = lazy(() => import("./pages/ChallengeHPEPage"));
const ChallengeHPERulesPage = lazy(() => import("./pages/ChallengeHPERulesPage"));
const DifficultClientPage = lazy(() => import("./pages/salon/DifficultClientPage"));
const OutsmartAIPage = lazy(() => import("./pages/OutsmartAIPage"));
const CertificationsPage = lazy(() => import("./pages/CertificationsPage"));
const TransformationChallengePage = lazy(() => import("./pages/TransformationChallengePage"));
const ChallengeBoardPage = lazy(() => import("./pages/ChallengeBoardPage"));
const MoneyZonePage = lazy(() => import("./pages/MoneyZonePage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const JoinPage = lazy(() => import("./pages/JoinPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const AdminInboxPage = lazy(() => import("./pages/AdminInboxPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const TransformationArenaPage = lazy(() => import("./pages/TransformationArenaPage"));
const TransformationContestantPage = lazy(() => import("./pages/TransformationContestantPage"));
const PRIMECanvasPage = lazy(() => import("./pages/PRIMECanvasPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const StripeTestPage = lazy(() => import("./pages/StripeTestPage"));
const AffiliateSignupPage = lazy(() => import("./pages/AffiliateSignupPage"));
const AffiliateDashboardPage = lazy(() => import("./pages/AffiliateDashboardPage"));
const AdminAffiliatesPage = lazy(() => import("./pages/AdminAffiliatesPage"));
const OnboardingQuestionnairePage = lazy(() => import("./pages/OnboardingQuestionnairePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const CustomizeStartPage = lazy(() => import("./pages/CustomizeStartPage"));
const CustomizePage = lazy(() => import("./pages/sites/CustomizePage"));
const SiteCheckoutPage = lazy(() => import("./pages/sites/SiteCheckoutPage"));
const SiteOnboardingPage = lazy(() => import("./pages/sites/SiteOnboardingPage"));
const SiteSubmittedPage = lazy(() => import("./pages/sites/SiteSubmittedPage"));
const MySitePage = lazy(() => import("./pages/sites/MySitePage"));
const PublicStylistSitePage = lazy(() => import("./pages/sites/PublicStylistSitePage"));
const AdminStylistSitesPage = lazy(() => import("./pages/admin/AdminStylistSitesPage"));
const AdminSitePreviewPage = lazy(() => import("./pages/admin/AdminSitePreviewPage"));
const ChoosePathPage = lazy(() => import("./pages/ChoosePathPage"));
const PathProfilePage = lazy(() => import("./pages/PathProfilePage"));
const RecommendationPage = lazy(() => import("./pages/RecommendationPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const ThankYouPage = lazy(() => import("./pages/ThankYouPage"));
const StylistOfferPage = lazy(() => import("./pages/StylistOfferPage"));
const EarlyAccessFunnelPage = lazy(() => import("./pages/EarlyAccessFunnelPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const EliteChallengePage = lazy(() => import("./pages/EliteChallengePage"));
const EliteChallengeContestantPage = lazy(() => import("./pages/EliteChallengeContestantPage"));
const BookPage = lazy(() => import("./pages/BookPage"));
const MembershipsPage = lazy(() => import("./pages/MembershipsPage"));
const MembershipAgreementPage = lazy(() => import("./pages/MembershipAgreementPage"));
const MembershipActivatedPage = lazy(() => import("./pages/MembershipActivatedPage"));
const MyMembershipPage = lazy(() => import("./pages/MyMembershipPage"));
const AdminMembershipAgreementsPage = lazy(() => import("./pages/AdminMembershipAgreementsPage"));
const AdminMembershipsPage = lazy(() => import("./pages/AdminMembershipsPage"));
const AppyHourApprenticeshipPage = lazy(() => import("./pages/AppyHourApprenticeshipPage"));
const WelcomePage = lazy(() => import("./pages/WelcomePage"));
const SalonDirectoryPage = lazy(() => import("./pages/SalonDirectoryPage"));
const StudioPreviewPage = lazy(() => import("./pages/StudioPreviewPage"));
const BookConfirmedPage = lazy(() => import("./pages/BookConfirmedPage"));
const BookColorDimensionPage = lazy(() => import("./pages/BookColorDimensionPage"));
const BookColorDimensionConfirmedPage = lazy(() => import("./pages/BookColorDimensionConfirmedPage"));
const OwnerBookingsPage = lazy(() => import("./pages/OwnerBookingsPage"));
const OwnerSchedulerPage = lazy(() => import("./pages/OwnerSchedulerPage"));
const StudioMessagesPage = lazy(() => import("./pages/StudioMessagesPage"));
const StudioRemindersPage = lazy(() => import("./pages/StudioRemindersPage"));
const StudioClickTrackingPage = lazy(() => import("./pages/StudioClickTrackingPage"));
const SalonAdminPage = lazy(() => import("./pages/SalonAdminPage"));
const BooksLandingPage = lazy(() => import("./pages/BooksLandingPage"));
const BooksLibraryPage = lazy(() => import("./pages/books/BooksLibraryPage"));
const BudgetBookPage = lazy(() => import("./pages/books/BudgetBookPage"));
const GoalsBookPage = lazy(() => import("./pages/books/GoalsBookPage"));
const DopamineBookPage = lazy(() => import("./pages/books/DopamineBookPage"));
const MoneyMomentsBookPage = lazy(() => import("./pages/books/MoneyMomentsBookPage"));
const GrowthBookPage = lazy(() => import("./pages/books/GrowthBookPage"));
const ClientDirectoryPage = lazy(() => import("./pages/books/ClientDirectoryPage"));
const KnowledgeAdminPage = lazy(() => import("./pages/admin/KnowledgeAdminPage"));
const AdminChallengeSubmissionsPage = lazy(() => import("./pages/admin/AdminChallengeSubmissionsPage"));
import { useReferralOnSignup } from "./hooks/useReferralOnSignup";

const queryClient = new QueryClient();

function ReferralHandler() {
  useReferralOnSignup();
  return null;
}

function ConditionalDisclaimer() {
  const { pathname } = useLocation();
  const HIDDEN = ["/early-access"];
  if (HIDDEN.includes(pathname)) return null;
  return <GlobalDisclaimer />;
}

const RouteFallback = () => (
  <div className="flex flex-1 items-center justify-center" aria-label="Loading">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-foreground/20 border-t-foreground" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ReferralHandler />
      <BrowserRouter>
        {/* Root shell intentionally follows document flow. Page-level experiences may
            opt into an inner scroller, but dashboard pages always use body scrolling. */}
        <div data-app-shell className="relative flex min-h-full flex-col bg-background">
          <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-md focus:bg-foreground focus:px-4 focus:py-2 focus:text-background">Skip to main content</a>
          <main id="main-content" className="flex min-w-0 flex-1 flex-col" role="main">
          <AnimatePresence mode="wait">
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<AskPage />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/explore" element={<HomePage />} />
              {/* AI intro moved to /start; /customize is now the stylist website product */}
              <Route path="/start" element={<CustomizeStartPage />} />
              <Route path="/customize" element={<CustomizePage />} />
              <Route path="/customize/checkout" element={<SiteCheckoutPage />} />
              <Route path="/site/onboarding" element={<ProtectedRoute authOnly><SiteOnboardingPage /></ProtectedRoute>} />
              <Route path="/site/submitted" element={<ProtectedRoute authOnly><SiteSubmittedPage /></ProtectedRoute>} />
              <Route path="/my-site" element={<ProtectedRoute authOnly><MySitePage /></ProtectedRoute>} />
              <Route path="/s/:slug" element={<PublicStylistSitePage />} />
              <Route path="/admin/sites" element={<ProtectedRoute authOnly><AdminStylistSitesPage /></ProtectedRoute>} />
              <Route path="/admin/sites/:slug/preview" element={<ProtectedRoute authOnly><AdminSitePreviewPage /></ProtectedRoute>} />

              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/stripe-test" element={<ProtectedRoute authOnly><StripeTestPage /></ProtectedRoute>} />
              <Route path="/dashboard" element={<Navigate to="/ask" replace />} />
              <Route path="/my-path" element={<ProtectedRoute authOnly><MyPathPage /></ProtectedRoute>} />
              <Route path="/choose-path" element={<ChoosePathPage />} />
              <Route path="/onboarding" element={<OnboardingQuestionnairePage />} />
              <Route path="/onboarding/profile" element={<ProtectedRoute authOnly><PathProfilePage /></ProtectedRoute>} />
              <Route path="/recommendation" element={<ProtectedRoute authOnly><RecommendationPage /></ProtectedRoute>} />
              <Route path="/transformations" element={<ProtectedRoute authOnly><TransformationsPage /></ProtectedRoute>} />
              <Route path="/leaderboards" element={<ProtectedRoute authOnly><LeaderboardsPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute authOnly><ProfilePage /></ProtectedRoute>} />

              {/* ── Stylist tier ── */}
              <Route path="/assistant" element={<ProtectedRoute minTier="stylist"><AssistantPage /></ProtectedRoute>} />
              <Route path="/ask" element={<AskPage />} />
              <Route path="/answer" element={<AnswerPage />} />
              <Route path="/saved" element={<ProtectedRoute minTier="stylist"><SavedPage /></ProtectedRoute>} />
              <Route path="/client-records" element={<ProtectedRoute minTier="stylist"><ClientRecordsPage /></ProtectedRoute>} />
              <Route path="/pro-decision" element={<ProtectedRoute minTier="stylist"><ProDecisionMode /></ProtectedRoute>} />
              <Route path="/bro-check" element={<ProtectedRoute minTier="stylist"><BROCheck /></ProtectedRoute>} />
              <Route path="/hug-finish" element={<ProtectedRoute minTier="stylist"><HUGFinish /></ProtectedRoute>} />
              <Route path="/keep-lifting" element={<ProtectedRoute minTier="stylist"><KeepLifting /></ProtectedRoute>} />
              <Route path="/finish-decision" element={<ProtectedRoute minTier="stylist"><FinishDecision /></ProtectedRoute>} />
              <Route path="/ice-finish" element={<ProtectedRoute minTier="stylist"><ICEFinish /></ProtectedRoute>} />
              <Route path="/perm-method" element={<PermMethodPage />} />

              {/* ── Pro Stylist tier ── */}
              <Route path="/hair-rescue" element={<ProtectedRoute minTier="pro_stylist"><HairRescuePage /></ProtectedRoute>} />
              <Route path="/certifications" element={<ProtectedRoute minTier="stylist"><CertificationsPage /></ProtectedRoute>} />
              <Route path="/challenge-board" element={<ProtectedRoute minTier="pro_stylist"><ChallengeBoardPage /></ProtectedRoute>} />

              {/* ── Pro Competitor tier ── */}
              <Route path="/fix-it" element={<ProtectedRoute minTier="pro_competitor"><FixItChallengePage /></ProtectedRoute>} />
              <Route path="/fix-it-challenge" element={<ProtectedRoute minTier="pro_competitor"><FixItChallengePage /></ProtectedRoute>} />
              <Route path="/fix-it-arena" element={<ProtectedRoute minTier="pro_competitor"><FixItArenaPage /></ProtectedRoute>} />
              <Route path="/fix-it-battle/:battleId" element={<ProtectedRoute minTier="pro_competitor"><FixItBattlePage /></ProtectedRoute>} />
              <Route path="/transformation-challenge" element={<ProtectedRoute minTier="pro_competitor"><TransformationChallengePage /></ProtectedRoute>} />
              <Route path="/money-zone" element={<ProtectedRoute minTier="pro_competitor"><MoneyZonePage /></ProtectedRoute>} />
              <Route path="/competition-entries" element={<ProtectedRoute minTier="pro_competitor"><CompetitionEntriesPage /></ProtectedRoute>} />
              <Route path="/model-release-form" element={<ProtectedRoute minTier="pro_competitor"><ModelReleaseFormPage /></ProtectedRoute>} />

              {/* ── Salon Portal (separate licensing, NOT tier-gated) ── */}
              <Route path="/salon-owner-portal" element={<SalonOwnerPortal />} />
              <Route path="/salon-owner-license" element={<SalonOwnerLicensePage />} />
              <Route path="/salon-checkout" element={<SalonCheckoutPage />} />
              <Route path="/checkout-salon-starter" element={<CheckoutSalonStarter />} />
              <Route path="/checkout-salon-professional" element={<CheckoutSalonProfessional />} />
              <Route path="/checkout-salon-elite" element={<CheckoutSalonElite />} />
              <Route path="/license-activated" element={<LicenseActivatedPage />} />
              <Route path="/verify-professional-license" element={<VerifyProfessionalLicense />} />
              <Route path="/salon/portal"       element={<SalonPortalPage />} />
              <Route path="/salon/stylists"     element={<MyStylistsPage />} />
              <Route path="/salon/training"     element={<SalonTrainingPage />} />
              <Route path="/salon/branding"     element={<SalonBrandingPage />} />
              <Route path="/salon/training/when-training-isnt-training" element={<WhenTrainingIsntTrainingPage />} />
              <Route path="/salon/training/difficult-client-complaint" element={<DifficultClientPage />} />
              <Route path="/salon/competitions" element={<CompetitionsPage />} />
              <Route path="/salon/hiring"       element={<HiringBoardPage />} />

              {/* ── Public / auth-only routes ── */}
              <Route path="/academy-leaderboard" element={<ProtectedRoute authOnly><AcademyLeaderboardPage /></ProtectedRoute>} />
              <Route path="/academy/:slug" element={<AcademyProfilePage />} />
              <Route path="/skill-profile" element={<ProtectedRoute authOnly><StylistSkillProfilePage /></ProtectedRoute>} />
              <Route path="/stylist/:userId" element={<StylistSkillProfilePage />} />
              <Route path="/verify-directory" element={<VerificationDirectoryPage />} />
              <Route path="/vote/:slug" element={<VotingPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/transformation" element={<ProtectedRoute authOnly><TransformationArenaPage /></ProtectedRoute>} />
              <Route path="/transformation/:slug" element={<ProtectedRoute authOnly><TransformationContestantPage /></ProtectedRoute>} />
              <Route path="/back-office" element={<ProtectedRoute authOnly><BackOfficePage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute authOnly><AdminBackOfficePage /></ProtectedRoute>} />
              <Route path="/admin/affiliates" element={<ProtectedRoute authOnly><AdminAffiliatesPage /></ProtectedRoute>} />
              <Route path="/verify-license" element={<VerifyLicenseForHPEPage />} />
              <Route path="/admin/verifications" element={<ProtectedRoute authOnly><AdminHPEVerificationsPage /></ProtectedRoute>} />
              <Route path="/admin/inbox" element={<ProtectedRoute authOnly><AdminInboxPage /></ProtectedRoute>} />
              <Route path="/challenge-hpe" element={<ProtectedRoute authOnly><ChallengeHPEPage /></ProtectedRoute>} />
              <Route path="/challenge-hpe-rules" element={<ChallengeHPERulesPage />} />
              <Route path="/outsmart-ai" element={<ProtectedRoute minTier="pro_competitor"><OutsmartAIPage /></ProtectedRoute>} />
              <Route path="/prime-your-canvas" element={<PRIMECanvasPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/join" element={<JoinPage />} />
              <Route path="/r/:inviteCode" element={<JoinPage />} />
              <Route path="/affiliate-signup" element={<AffiliateSignupPage />} />
              <Route path="/affiliate-dashboard" element={<ProtectedRoute authOnly><AffiliateDashboardPage /></ProtectedRoute>} />
              <Route path="/account" element={<ProtectedRoute authOnly><AccountPage /></ProtectedRoute>} />
              <Route path="/thank-you" element={<ThankYouPage />} />
              <Route path="/stylist" element={<StylistOfferPage />} />
              <Route path="/priority-list" element={<Navigate to="/early-access" replace />} />
              <Route path="/notify-me" element={<Navigate to="/early-access" replace />} />
              <Route path="/early-access" element={<CheckoutGate><EarlyAccessFunnelPage /></CheckoutGate>} />
              <Route path="/stylist-access" element={<Navigate to="/early-access" replace />} />
              <Route path="/starlie" element={<StarlieLandingPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/faq" element={<FaqPage />} />
              {guides.map((g) => <Route key={g.path} path={g.path} element={<GuidePage guide={g} />} />)}
              <Route path="/elite-challenge" element={<EliteChallengePage />} />
              <Route path="/elite-challenge/c/:code" element={<EliteChallengeContestantPage />} />
              {/* ── Booking MVP ── */}
              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/studio-preview" element={<StudioPreviewPage />} />
              <Route path="/book" element={<BookPage />} />
              <Route path="/studio/transformations" element={<BookPage />} />
              <Route path="/studio/services" element={<BookPage />} />
              <Route path="/studio/memberships" element={<MembershipsPage />} />
              <Route path="/memberships" element={<MembershipsPage />} />
              <Route path="/membership-agreement" element={<MembershipAgreementPage />} />
              <Route path="/membership/activated" element={<MembershipActivatedPage />} />
              <Route path="/my-membership" element={<ProtectedRoute authOnly><MyMembershipPage /></ProtectedRoute>} />
              <Route path="/admin/membership-agreements" element={<ProtectedRoute authOnly><AdminMembershipAgreementsPage /></ProtectedRoute>} />
              <Route path="/admin/memberships" element={<ProtectedRoute authOnly><AdminMembershipsPage /></ProtectedRoute>} />
              <Route path="/apprenticeship" element={<AppyHourApprenticeshipPage />} />
              <Route path="/appy-hour" element={<AppyHourApprenticeshipPage />} />
              <Route path="/book/confirmed" element={<BookConfirmedPage />} />
              <Route path="/book/color-dimension" element={<BookColorDimensionPage />} />
              <Route path="/book/color-dimension/confirmed" element={<BookColorDimensionConfirmedPage />} />
              <Route path="/dashboard/schedule" element={<ProtectedRoute authOnly><OwnerSchedulerPage /></ProtectedRoute>} />
              <Route path="/salon/scheduler" element={<ProtectedRoute authOnly><OwnerSchedulerPage /></ProtectedRoute>} />
              <Route path="/dashboard/bookings" element={<ProtectedRoute authOnly><OwnerBookingsPage /></ProtectedRoute>} />
              <Route path="/salon/bookings" element={<ProtectedRoute authOnly><OwnerBookingsPage /></ProtectedRoute>} />
              <Route path="/dashboard/services" element={<ProtectedRoute authOnly><SalonAdminPage /></ProtectedRoute>} />
              <Route path="/dashboard/messages" element={<ProtectedRoute authOnly><StudioMessagesPage /></ProtectedRoute>} />
              <Route path="/salon/messages" element={<ProtectedRoute authOnly><StudioMessagesPage /></ProtectedRoute>} />
              <Route path="/messages" element={<Navigate to="/dashboard/messages" replace />} />
              <Route path="/msgs" element={<Navigate to="/dashboard/messages" replace />} />
              <Route path="/Msgs" element={<Navigate to="/dashboard/messages" replace />} />
              <Route path="/dashboard/reminders" element={<ProtectedRoute authOnly><StudioRemindersPage /></ProtectedRoute>} />
              <Route path="/dashboard/click-tracking" element={<ProtectedRoute authOnly><StudioClickTrackingPage /></ProtectedRoute>} />
              {/* ── Books & Business Tools — public marketing landing ── */}
              <Route path="/books" element={<BooksLandingPage />} />
              <Route path="/books/library" element={<ProtectedRoute minTier="stylist"><BooksLibraryPage /></ProtectedRoute>} />
              <Route path="/books/budget" element={<ProtectedRoute minTier="stylist"><BudgetBookPage /></ProtectedRoute>} />
              <Route path="/books/goals" element={<ProtectedRoute minTier="stylist"><GoalsBookPage /></ProtectedRoute>} />
              <Route path="/books/dopamine" element={<ProtectedRoute minTier="stylist"><DopamineBookPage /></ProtectedRoute>} />
              <Route path="/books/money-moments" element={<ProtectedRoute minTier="stylist"><MoneyMomentsBookPage /></ProtectedRoute>} />
              <Route path="/books/growth" element={<ProtectedRoute minTier="stylist"><GrowthBookPage /></ProtectedRoute>} />
              <Route path="/books/clients" element={<ClientDirectoryPage />} />
              <Route path="/admin/knowledge" element={<ProtectedRoute authOnly><KnowledgeAdminPage /></ProtectedRoute>} />
              <Route path="/admin/challenges" element={<ProtectedRoute authOnly><AdminChallengeSubmissionsPage /></ProtectedRoute>} />
              {/* Studio Starlie & Co. — primary public landing page */}
              <Route path="/studiostarlie" element={<WelcomePage />} />
              {/* Salon Directory — showcase featured salon experiences */}
              <Route path="/directory" element={<SalonDirectoryPage />} />
              {/* Vanity slug route — must be right before catch-all */}
              <Route path="/:slug" element={<VotingPage />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </AnimatePresence>
          </main>
          {/* Global disclaimer sits in normal flow, nav clears it */}
          <ConditionalDisclaimer />
        </div>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
